<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import TextWidget from '../widgets/TextWidget.vue';
    import PreviewGraphWidget from './PreviewGraphWidget.vue';
    import {
        isTextFormWidget,
        isGraphFormWidget,
        isPromptWidget,
        isTopListWidget,
        isStaticGraphWidget,
        isGraphWidget,
    } from '@/js/utils/widgetUtils';
    import { useWidgetFormStore } from '@/js/stores/widgetForm';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import { computed, onBeforeUnmount, ref, watch } from 'vue';
    import { ICreateWidget, IWidgetPreview } from '@/js/services/api/widget/types';
    import PromptWidget from '../widgets/PromptWidget.vue';
    import TopListWidget from '../widgets/TopListWidget.vue';
    import StaticGraphWidget from '../widgets/StaticGraphWidget.vue';
    import { EchoFilterRequest, FilterStoreName } from '@/js/services/api/filter/types';
    import ActiveFiltersContainer from '../filter/ActiveFiltersContainer.vue';
    import LoadingDots from '../LoadingDots.vue';
    import InvalidFilterEmptyState from '../widgets/InvalidFilterEmptyState.vue';
    import { useQuery } from '@tanstack/vue-query';
    import { QK } from '@/js/consts/query-keys';
    import { widgetPreview } from '@/js/services/api/widget/widgetApi';

    const widgetFormStore = useWidgetFormStore();
    const filterStore = useFilterStoreByName(FilterStoreName.WidgetFilter);
    const widget = computed(() => widgetFormStore.widget);

    const delayedRequestData = ref<EchoFilterRequest>(filterStore.requestData);
    const delayedLoading = ref(false);
    let delayedRequestDataTimeout: number | undefined;

    watch(
        () => filterStore.requestData,
        (newValue, oldValue) => {
            if (JSON.stringify(newValue) === JSON.stringify(oldValue)) {
                return;
            }

            if (delayedRequestDataTimeout) {
                clearTimeout(delayedRequestDataTimeout);
            }
            delayedLoading.value = true;

            delayedRequestDataTimeout = window.setTimeout(() => {
                delayedRequestData.value = newValue;
                delayedLoading.value = false;
            }, 1000);
        }
    );

    onBeforeUnmount(() => {
        if (delayedRequestDataTimeout) {
            clearTimeout(delayedRequestDataTimeout);
        }
    });

    const isGraphEnabled = computed(
        () =>
            isGraphFormWidget(widget.value) &&
            !!(widget.value as ICreateWidget<'Graph'>).kpiVisualizationTypeId &&
            !!(widget.value as ICreateWidget<'Graph'>).graphType
    );

    const placeholderData = computed<IWidgetPreview | undefined>(() => {
        const w = widgetFormStore.originalWidgetData;
        if (w && isGraphWidget(w) && w.graphWidget?.graphData) {
            return w.graphWidget.graphData as IWidgetPreview;
        }
        return undefined;
    });

    const { data: graphDataPreview, isLoading: queryLoading } = useQuery({
        queryKey: computed(() => [
            QK.WIDGET_PREVIEW.GET,
            (widget.value as ICreateWidget<'Graph'>).kpiVisualizationTypeId,
            (widget.value as ICreateWidget<'Graph'>).graphType,
            delayedRequestData.value,
        ]),
        queryFn: ({ signal }) => {
            const w = widget.value as ICreateWidget<'Graph'>;
            return widgetPreview(
                {
                    kpiWidget: { visualizationTypeId: w.kpiVisualizationTypeId },
                    filter: delayedRequestData.value,
                    graphType: w.graphType!,
                },
                signal
            ).then((r) => r.data);
        },
        enabled: isGraphEnabled,
        placeholderData: placeholderData,
    });

    const loading = computed(() => delayedLoading.value || queryLoading.value);
</script>
<template>
    <div class="bg-white border-b border-gray-light">
        <h1 class="heading-sm mx-5 mt-5 mb-3">
            {{ __('widget-form-preview-title') }}
        </h1>
        <div v-if="isGraphFormWidget(widget)" class="w-full flex">
            <ActiveFiltersContainer :filter-store-name="FilterStoreName.WidgetFilter" />
        </div>
    </div>
    <div class="flex-grow overflow-auto custom-scrollbar">
        <div class="min-h-full w-full flex items-center justify-center">
            <div class="w-full sm:max-w-2xl">
                <div class="p-6">
                    <div
                        class="bg-white w-full rounded-[14px] p-8 shadow-md grid grid-cols-1 grid-rows-[1fr_auto] gap-6 min-h-[50vh]"
                    >
                        <div class="border-1 border-gray-light rounded-[8px] overflow-hidden">
                            <LoadingDots v-if="loading" class="h-full" />

                            <template v-else>
                                <TextWidget v-if="isTextFormWidget(widget)" :widget="widget" />
                                <InvalidFilterEmptyState
                                    v-if="
                                        isGraphFormWidget(widget) &&
                                        filterStore.all?.levelFilterMatchesNothing
                                    "
                                    removable
                                    @remove="filterStore.removeInvalidLevelFilter()"
                                />
                                <PreviewGraphWidget
                                    v-else-if="
                                        isGraphFormWidget(widget) &&
                                        graphDataPreview &&
                                        isGraphEnabled
                                    "
                                    :key="JSON.stringify(graphDataPreview)"
                                    :widget="widget"
                                    :preview="graphDataPreview"
                                />
                                <div
                                    v-else-if="isGraphFormWidget(widget) && !isGraphEnabled"
                                    class="flex items-center justify-center h-full p-8"
                                >
                                    <p class="text-body text-gray-dark text-center">
                                        {{ __('widget-form-preview-fill-fields') }}
                                    </p>
                                </div>
                                <PromptWidget
                                    v-if="
                                        widgetFormStore.originalWidgetData &&
                                        isPromptWidget(widgetFormStore.originalWidgetData)
                                    "
                                    :widget="widgetFormStore.originalWidgetData"
                                />
                                <div
                                    v-if="
                                        widgetFormStore.originalWidgetData &&
                                        isTopListWidget(widgetFormStore.originalWidgetData)
                                    "
                                    class="px-4"
                                >
                                    <TopListWidget :widget="widgetFormStore.originalWidgetData" />
                                </div>
                                <StaticGraphWidget
                                    v-if="
                                        widgetFormStore.originalWidgetData &&
                                        isStaticGraphWidget(widgetFormStore.originalWidgetData)
                                    "
                                    :widget="widgetFormStore.originalWidgetData"
                                />
                            </template>
                        </div>
                        <div>
                            <h1 class="heading-sm">
                                {{
                                    widget.title ? widget.title : __('widget-form-name-input-title')
                                }}
                            </h1>
                            <p class="text-body">
                                {{
                                    widget.description
                                        ? widget.description
                                        : __('widget-form-preview-description')
                                }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
